/* ============================================================
   cort-quiz-cx1.js — Cortisol Armor Quiz state machine
   Forked 2026-06-11 from cx-quiz.js (engine architecture, warm-cart,
   form-POST checkout preserved verbatim). New: multi-select chips,
   affirm interpolation, persona results, fixed 3-SKU stack.
   Tracker: reuses cx-quiz-tracker.js (cxTrack) with quiz_id merged
   into every payload → cx_quiz_events discriminator.
   ============================================================ */
(function () {
  "use strict";

  const QUIZ_ID = "cort-quiz-cx1";
  /* q0 welcome · q1 hook · q2 tune · q3 sleep · q4 impact · q5 stress · q6 coffee · q7 supps · q8 win · q9 email · q10 loading · q11 results */
  const SCREENS = ["q0", "q1", "q2", "q3", "q4", "q5", "q6", "q7", "q8", "q9", "q10", "q11"];
  const LOADING_DELAY_MS = 3200;
  const TOTAL_QS = 8;

  /* Win persona tags (Q8) */
  const WIN_TAGS = {
    "april":    "Menopause",
    "jennifer": "Performance",
    "lynne":    "Busy Days",
    "debra":    "Better Aging"
  };

  /* Fixed stack — Sleep + Energy + Essentials, monthly sub. Beauty excluded. */
  const STACK = ["sleep", "energy", "essentials"];

  const DISCOUNT_CODES = { bundle_3: "NUBUNDLE3", wellness_three: "NUWELL15" };

  const PRICE_TABLE = {
    sleep:      { otp: 39.99, sub: 28.99 },
    energy:     { otp: 39.99, sub: 28.99 },
    essentials: { otp: 39.99, sub: 28.99 }
  };
  const BUNDLE_3_PCT = 20;
  const WELLNESS_BONUS_THREE = 15;
  const round = n => Math.round(n * 100) / 100;
  const fmt = n => "$" + n.toFixed(2);

  const PRODUCT_CONFIG_DEFAULT = {
    shop_domain: "nustrips.myshopify.com",
    monthly_selling_plan: "4617470167",
    variants: {
      sleep:      "44052420755671",
      energy:     "48418858827991",
      essentials: "46996555563223"
    }
  };

  /* Q1 chip value → affirm clause */
  const TRIGGER_LABELS = {
    "overworked":    "you're overworked",
    "short-sleep":   "you don't get enough sleep",
    "eating":        "you're not eating right",
    "deadlines":     "deadlines stack up",
    "doomscrolling": "you're doomscrolling at 1am",
    "always":        "life just doesn't let up"
  };

  function joinClauses(arr) {
    if (!arr.length) return "life piles up";
    if (arr.length === 1) return arr[0];
    return arr.slice(0, -1).join(", ") + " & " + arr[arr.length - 1];
  }

  function init(root) {
    if (!root) return;
    const stage   = root.querySelector("[data-ctq-stage]");
    const screens = Array.from(root.querySelectorAll("[data-ctq-screen]"));
    const state   = { idx: 0, answers: {}, subscribe: true, stepEnteredAt: Date.now() };

    const track = (name, payload) => {
      try {
        if (window.cxTrack) window.cxTrack(name, Object.assign({ quiz_id: QUIZ_ID }, payload || {}));
      } catch (e) {}
    };

    track("cx_quiz_started", {});
    track("cx_quiz_step_viewed", { step: "q0" });

    window.addEventListener("beforeunload", function () {
      const reached = window.cxQuizResultsViewed && window.cxQuizResultsViewed();
      if (!reached) {
        track("cx_quiz_abandoned", {
          last_step: SCREENS[state.idx],
          time_in_session_ms: Date.now() - (state.stepEnteredAt || Date.now())
        });
      }
    });

    /* ---------- navigation ---------- */
    function go(targetId) {
      const nextIdx = SCREENS.indexOf(targetId);
      if (nextIdx === -1) return;
      const next = screens[nextIdx];
      if (!next) return;

      screens.forEach(s => { if (s !== next) s.classList.remove("is-active"); });
      next.classList.add("is-active");
      state.idx = nextIdx;
      updateProgress();

      track("cx_quiz_step_viewed", { step: targetId });
      state.stepEnteredAt = Date.now();

      renderNugget(targetId);

      if (targetId === "q10") {
        track("cx_quiz_loading_viewed", {});
        setTimeout(() => go("q11"), LOADING_DELAY_MS);
      }

      if (targetId === "q11") {
        renderResults();
        // Warm the Shopify cart: 3 SKUs + monthly plan + both codes, fire-and-forget.
        (async () => {
          try {
            const cfg = (window.CTQ_CONFIG && window.CTQ_CONFIG.variants) ? window.CTQ_CONFIG : PRODUCT_CONFIG_DEFAULT;
            await warmCart(cfg, STACK.map(p => ({ product: p, qty: 1 })), true, computeCartDiscountCodes());
          } catch (e) { console.warn("[ctq] warmCart err:", e); }
        })();
        track("cx_quiz_results_viewed", {
          recommended_stack: STACK.join("+"),
          answers: state.answers
        });
      }

      if (stage && stage.scrollIntoView) stage.scrollIntoView({ block: "start", behavior: "instant" });
    }

    function updateProgress() {
      const labelMap = { q1: 1, q2: 2, q3: 3, q4: 4, q5: 5, q6: 6, q7: 7, q8: 8 };
      const cur = SCREENS[state.idx];
      const stepNum = labelMap[cur];
      screens.forEach(s => {
        const fill = s.querySelector("[data-ctq-progress-fill]");
        if (fill) {
          let pct = 100;
          if (cur === "q0") pct = 0;
          else if (stepNum) pct = (stepNum / TOTAL_QS) * 100;
          fill.style.width = pct + "%";
        }
      });
    }

    /* ---------- affirm banner (Q2) ---------- */
    /* ---------- education nuggets — supportive micro-affirmations per screen,
       each reacting to the PREVIOUS answer. All copy from fixed maps (no user text). ---------- */
    const NUGGETS = {
      q1: function (a) {
        const map = {
          "20s-30s": "Smart timing — cortisol habits built now pay compounding dividends for decades.",
          "40s":     "Your 40s are when cortisol starts overstaying its welcome. Perfect moment to get ahead of it.",
          "50s":     "In your 50s, hormone shifts can amplify cortisol's effects. You're in exactly the right place.",
          "60s":     "After 60, recovery matters more than ever — and cortisol is recovery's biggest tax."
        };
        const body = map[a.q0];
        return body ? { lead: "Good to meet you ✓", body: " — " + body } : null;
      },
      q2: function (a) {
        const raw = a.q1 || [];
        const picks = raw.map(v => TRIGGER_LABELS[v]).filter(Boolean);
        if (!picks.length) return null;
        const body = raw.includes("always")
          ? " — when life doesn't let up, cortisol runs the show. You're not imagining it, and you're definitely not alone."
          : " — cortisol loves it when " + joinClauses(picks) + ". You're not imagining it, and you're definitely not alone.";
        return { lead: "Called it ✓", body: body };
      },
      q3: function (a) {
        const map = {
          "menopause":        ["Thank you for sharing ✓", "estrogen shifts can amplify cortisol's downstream effects. You're not imagining any of it."],
          "postpartum":       ["Huge season ✓", "sleep debt plus new-baby stress is the heaviest cortisol load there is. Be kind to yourself."],
          "aging":            ["Very common ✓", "deep sleep naturally declines with age — and cortisol quietly fills the gap."],
          "off-rx":           ["That takes guts ✓", "gentle, non-habit-forming support matters most right here."],
          "stress-season":    ["We hear you ✓", "heavy seasons leave a real chemical mark. It's not 'just in your head.'"],
          "long-covid":       ["Respect ✓", "recovery is a marathon. Supporting sleep and reserves is exactly the right instinct."],
          "hard-year":        ["We hear you ✓", "hard years rewire stress responses — and they can be rewired back."],
          "nothing-specific": ["Honestly the best reason ✓", "prevention beats repair, every time."]
        };
        const m = map[a.q2];
        return m ? { lead: m[0], body: " — " + m[1] } : null;
      },
      q4: function (a) {
        const map = {
          "enough":     ["Love that ✓", "good sleep is your strongest cortisol buffer. Most people are rebuilding — you get to defend."],
          "erratic":    ["That tracks ✓", "erratic sleep and cortisol feed each other in a loop. Break one side and the other loosens."],
          "cant-sleep": ["That tracks ✓", "the 1am mental-tax session is the classic elevated-cortisol signature. It's chemical, not character."]
        };
        const m = map[a.q3];
        return m ? { lead: m[0], body: " — " + m[1] } : null;
      },
      q5: function (a) {
        const n = (a.q4 || []).length;
        if (!n) return null;
        const what = n === 1 ? "that's a textbook cortisol signature" : "those are textbook cortisol signatures";
        return { lead: "Makes sense ✓", body: " — " + what + ". It's connected, not coincidence." };
      },
      q6: function (a) {
        const v = parseInt(a.q5 || "0", 10);
        if (!v) return null;
        if (v <= 2) return { lead: "Respect ✓", body: " — love that for you. Let's make sure it stays there." };
        if (v === 3) return { lead: "Honest answer ✓", body: " — the middle zone is sneaky. It compounds quietly while you're busy coping." };
        return { lead: "Thank you for being honest ✓", body: " — running at " + v + "/5 is heavy. L-theanine is clinically shown to take the edge off without sedating you." };
      },
      q7: function (a) {
        const map = {
          "none":          ["Respect ✓", "rare and impressive — your caffeine baseline is gold."],
          "one-two":       ["Noted ✓", "civilized indeed. Timing matters more than quantity — late caffeine is cortisol's best friend."],
          "three-plus":    ["No judgment ✓", "caffeine works. Pairing it with L-theanine keeps the lift and drops the jitters."],
          "energy-drinks": ["No judgment ✓", "the sugar + caffeine combo is the hardest crash in the game. There's a smoother way to the same energy."]
        };
        const m = map[a.q6];
        return m ? { lead: m[0], body: " — " + m[1] } : null;
      },
      q8: function (a) {
        const picks = a.q7 || [];
        if (!picks.length) return null;
        if (picks.indexOf("nothing") !== -1) return { lead: "Perfect ✓", body: " — a clean slate is honestly the easiest place to start. Nothing to untangle." };
        if (picks.indexOf("melatonin") !== -1) return { lead: "Good instincts ✓", body: " — with melatonin, delivery is usually the weak link, not the ingredient." };
        return { lead: "Good instincts ✓", body: " — you're already investing in yourself. Let's make every milligram absorb better." };
      }
    };

    function renderNugget(screenId) {
      const fn = NUGGETS[screenId];
      const el = root.querySelector('[data-ctq-affirm="' + screenId + '"]');
      if (!el) return;
      const msg = fn ? fn(state.answers) : null;
      while (el.firstChild) el.removeChild(el.firstChild);
      if (!msg) { el.style.display = "none"; return; }
      el.style.display = "";
      const b = document.createElement("strong");
      b.textContent = msg.lead;
      el.appendChild(b);
      el.appendChild(document.createTextNode(msg.body));
    }

    /* ---------- results personalization ---------- */
    function buildPersona() {
      const stress = parseInt(state.answers.q5 || "0", 10);
      const coffee = state.answers.q6 || "";
      const sleep  = state.answers.q3 || "";
      const parts = [];
      if (stress >= 4) parts.push("maxed-out");
      else if (stress === 3) parts.push("stretched-thin");
      if (coffee === "three-plus") parts.push("three-coffee");
      else if (coffee === "energy-drinks") parts.push("energy-drink");
      if (sleep === "erratic") parts.push("rough-sleeper");
      else if (sleep === "cant-sleep") parts.push("3am-waker");
      if (!parts.length) return "For the cortisol-aware.";
      return "For the " + parts.join(", ") + ".";
    }

    function buildEnergyLine() {
      const coffee = state.answers.q6 || "";
      if (coffee === "three-plus")
        return "“You said 3+ coffees a day — swap the back half for a strip. Caffeine smoothed by L-theanine: faster, jitter-free mornings and a whole other level of alertness. No spike, no crash.”";
      if (coffee === "energy-drinks")
        return "“Energy drinks spike you, then bill you. Clean caffeine + L-theanine gives the same lift — jitter-free, crash-free, no sugar.”";
      return "“Clean caffeine + L-theanine: faster, jitter-free mornings and steady focus — no spike-crash cycle.”";
    }

    function buildSleepLine() {
      const sleep = state.answers.q3 || "";
      if (sleep === "erratic" || sleep === "cant-sleep")
        return "“You flagged rough nights. Melatonin + valerian land in minutes — 89% fall asleep faster than with pills, 87% report deeper, more restful sleep.”";
      return "“Even good sleepers run hot under stress. 89% fall asleep faster than with pills; 87% report deeper, more restful sleep.”";
    }

    function renderResults() {
      setText("[data-ctq-persona]", buildPersona());
      setText("[data-ctq-line-energy]", buildEnergyLine());
      setText("[data-ctq-line-sleep]", buildSleepLine());
      // Win tag → results eyebrow ("Built for you — Menopause plan")
      const win = state.answers.q8;
      if (win && win.tag) setText("[data-ctq-r-eyebrow]", "Built for you — " + win.tag + " plan");
      updateReceipt();
    }

    function setText(sel, value) {
      const el = root.querySelector(sel);
      if (el) el.textContent = value;
    }

    /* ---------- receipt (fixed 3-SKU stack, monthly sub) ---------- */
    function updateReceipt() {
      const subtotalOtp = round(STACK.reduce((s, k) => s + PRICE_TABLE[k].otp, 0)); // 119.97
      const afterSub    = round(STACK.reduce((s, k) => s + PRICE_TABLE[k].sub, 0)); //  86.97
      const bundleAmt   = round(STACK.reduce((s, k) => s + Math.floor(PRICE_TABLE[k].sub * (BUNDLE_3_PCT / 100) * 100) / 100, 0)); // 17.37
      const finalTotal  = round(afterSub - bundleAmt - WELLNESS_BONUS_THREE); // 54.60
      const pct         = Math.round(((subtotalOtp - finalTotal) / subtotalOtp) * 100); // 54
      setText("[data-ctq-strike]", fmt(subtotalOtp) + "/mo");
      setText("[data-ctq-total]", fmt(finalTotal) + "/mo");
      setText("[data-ctq-save-pct]", "SAVE " + pct + "%");
      const ctaPrice = root.querySelector("[data-ctq-cta-price]");
      if (ctaPrice) ctaPrice.textContent = "ADD STACK TO CART · " + fmt(finalTotal) + "/mo";
    }

    function computeCartDiscountCodes() {
      return [DISCOUNT_CODES.bundle_3, DISCOUNT_CODES.wellness_three];
    }

    /* ---------- tap handlers ---------- */
    root.addEventListener("click", function (e) {
      // Win card (Q8) — single-select card, then CTA advances
      const winCard = e.target.closest("[data-ctq-win]");
      if (winCard) {
        e.preventDefault();
        const screen = winCard.closest("[data-ctq-screen]");
        screen.querySelectorAll("[data-ctq-win]").forEach(c => c.classList.toggle("is-on", c === winCard));
        const cta = screen.querySelector("[data-ctq-win-continue]");
        const label = screen.querySelector("[data-ctq-win-cta]");
        if (cta) cta.disabled = false;
        if (label) label.textContent = "BUILD " + (winCard.getAttribute("data-ctq-name") || "MY") + "'S STACK →";
        return;
      }

      // Win continue (Q8 → email)
      const winCont = e.target.closest("[data-ctq-win-continue]");
      if (winCont) {
        e.preventDefault();
        const screen = winCont.closest("[data-ctq-screen]");
        const sel = screen.querySelector("[data-ctq-win].is-on");
        if (!sel) return;
        const persona = sel.getAttribute("data-ctq-win");
        state.answers.q8 = { persona: persona, tag: WIN_TAGS[persona] || persona };
        track("cx_quiz_step_answered", {
          step: "q8", answer: persona,
          step_dwell_ms: Date.now() - (state.stepEnteredAt || Date.now())
        });
        go(winCont.getAttribute("data-ctq-next"));
        return;
      }

      // Multi-select chip toggle
      const chip = e.target.closest("[data-ctq-chip]");
      if (chip) {
        e.preventDefault();
        const screen = chip.closest("[data-ctq-screen]");
        const exclusive = chip.hasAttribute("data-ctq-exclusive");
        if (exclusive) {
          screen.querySelectorAll("[data-ctq-chip]").forEach(c => { if (c !== chip) c.classList.remove("is-on"); });
          chip.classList.toggle("is-on");
        } else {
          screen.querySelectorAll("[data-ctq-chip][data-ctq-exclusive]").forEach(c => c.classList.remove("is-on"));
          chip.classList.toggle("is-on");
        }
        const cont = screen.querySelector("[data-ctq-continue]");
        if (cont) cont.disabled = screen.querySelectorAll("[data-ctq-chip].is-on").length === 0;
        return;
      }

      // Multi-select continue
      const cont = e.target.closest("[data-ctq-continue]");
      if (cont) {
        e.preventDefault();
        const screen = cont.closest("[data-ctq-screen]");
        const screenId = screen.getAttribute("data-ctq-screen");
        const picks = Array.from(screen.querySelectorAll("[data-ctq-chip].is-on")).map(c => c.getAttribute("data-ctq-value"));
        if (!picks.length) return;
        state.answers[screenId] = picks;
        track("cx_quiz_step_answered", {
          step: screenId, answer: picks.join(","),
          step_dwell_ms: Date.now() - (state.stepEnteredAt || Date.now())
        });
        go(cont.getAttribute("data-ctq-next"));
        return;
      }

      // Single-select advance (excludes all dedicated-continue buttons — they have their own handlers)
      const tap = e.target.closest("[data-ctq-next]:not([data-ctq-continue]):not([data-ctq-stress-continue]):not([data-ctq-win-continue])");
      if (tap) {
        e.preventDefault();
        const screen = tap.closest("[data-ctq-screen]");
        const screenId = screen ? screen.getAttribute("data-ctq-screen") : null;
        const value = tap.getAttribute("data-ctq-value") || (tap.textContent || "").trim();
        if (screenId && value) {
          state.answers[screenId] = value;
          track("cx_quiz_step_answered", {
            step: screenId, answer: value,
            step_dwell_ms: Date.now() - (state.stepEnteredAt || Date.now())
          });
        }
        go(tap.getAttribute("data-ctq-next"));
        return;
      }

      const addBtn = e.target.closest("[data-ctq-add-to-cart]");
      if (addBtn) { e.preventDefault(); addToCart(); return; }
    });

    /* ---------- stress slider (Q5) ---------- */
    const STRESS_FACES = { 1: ["💆", "zen"], 2: ["🙂", "fine"], 3: ["😬", "tense"], 4: ["😮‍💨", "a lot"], 5: ["🤯", "maxed"] };
    const stressSlider = root.querySelector("[data-ctq-stress-slider]");
    if (stressSlider) {
      const emojiEl = root.querySelector("[data-ctq-stress-emoji]");
      const labelEl = root.querySelector("[data-ctq-stress-label]");
      const paint = () => {
        const v = parseInt(stressSlider.value, 10);
        const f = STRESS_FACES[v] || STRESS_FACES[3];
        if (emojiEl) { emojiEl.textContent = f[0]; emojiEl.classList.remove("is-pop"); void emojiEl.offsetWidth; emojiEl.classList.add("is-pop"); }
        if (labelEl) labelEl.textContent = v + " · " + f[1];
        const pct = ((v - 1) / 4) * 100;
        stressSlider.style.setProperty("--ctq-slider-pct", pct + "%");
      };
      stressSlider.addEventListener("input", paint);
      paint();
      const stressCont = root.querySelector("[data-ctq-stress-continue]");
      if (stressCont) {
        stressCont.addEventListener("click", function (e) {
          e.preventDefault();
          const v = String(parseInt(stressSlider.value, 10));
          state.answers.q5 = v;
          track("cx_quiz_step_answered", {
            step: "q5", answer: v,
            step_dwell_ms: Date.now() - (state.stepEnteredAt || Date.now())
          });
          go(stressCont.getAttribute("data-ctq-next"));
        });
      }
    }

    /* ---------- email gate ---------- */
    const emailForm = root.querySelector("[data-ctq-email-form]");
    if (emailForm) {
      emailForm.addEventListener("submit", function (e) {
        e.preventDefault();
        const emailInput = emailForm.querySelector('input[type="email"]');
        const email = emailInput ? emailInput.value : "";
        state.answers.email = email;
        track("cx_quiz_email_captured", {
          email: email,
          full_answers: state.answers,
          recommended_stack: STACK.join("+")
        });
        go("q10");
      });
    }

    /* ---------- Shopify cart machinery (verbatim from cx-quiz.js) ---------- */
    function isOnShopify() {
      const h = location.hostname || "";
      return h.endsWith(".myshopify.com") || h === "nustrips.com" || h.endsWith(".nustrips.com");
    }

    function buildCartUrl(cfg, items, subscribe, codes) {
      const lines = items.map(it => {
        const vid = cfg.variants[it.product];
        return vid ? (vid + ":" + it.qty) : null;
      }).filter(Boolean);
      if (!lines.length) return null;
      const params = [];
      if (subscribe && cfg.monthly_selling_plan) params.push("selling_plan=" + encodeURIComponent(cfg.monthly_selling_plan));
      if (codes.length) params.push("discount=" + encodeURIComponent(codes.join(",")));
      const qs = params.length ? "?" + params.join("&") : "";
      return "https://" + cfg.shop_domain + "/cart/" + lines.join(",") + qs;
    }

    function suppressSlideCart() {
      const close = () => {
        try {
          if (typeof window.upcartCloseCart === "function") window.upcartCloseCart();
          document.body.classList.remove(
            "upcartPopupShow", "upcart-show", "cart-drawer-open",
            "drawer-open", "mini-cart-open", "js-drawer-open"
          );
        } catch (e) {}
      };
      close();
      const id = setInterval(close, 150);
      setTimeout(() => clearInterval(id), 5000);
    }

    function maskUpcart(on) {
      const ID = "ctq-upcart-mask";
      let el = document.getElementById(ID);
      if (on && !el) {
        el = document.createElement("style");
        el.id = ID;
        el.textContent =
          '[id^="upcart-"],[class*="upcart-cart"],[class*="upcart-popup"],' +
          '[class*="upcart-drawer"],#upcart-app,.upcart-checkout' +
          '{display:none!important;opacity:0!important;' +
          'transition:none!important;pointer-events:none!important;}';
        document.head.appendChild(el);
      } else if (!on && el) {
        el.remove();
      }
    }

    async function warmCart(cfg, items, subscribe, codes) {
      if (!isOnShopify()) return;
      const planId = cfg.monthly_selling_plan ? parseInt(cfg.monthly_selling_plan, 10) : null;
      const adds = items.map(it => {
        const vid = parseInt(cfg.variants[it.product], 10);
        if (!vid) return null;
        const line = { id: vid, quantity: it.qty };
        if (subscribe && planId) line.selling_plan = planId;
        return line;
      }).filter(Boolean);
      if (!adds.length) return;
      maskUpcart(true);
      try {
        try {
          await fetch("/cart/clear.js", { method: "POST", headers: { "Accept": "application/json" } });
        } catch (e) { console.warn("[ctq] warmCart clear failed:", e); return; }
        suppressSlideCart();
        try {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({ items: adds })
          });
        } catch (e) { console.warn("[ctq] warmCart add failed:", e); return; }
        suppressSlideCart();
        for (let i = 0; i < codes.length; i++) {
          if (i > 0) await new Promise(r => setTimeout(r, 800));
          try {
            await fetch("/discount/" + encodeURIComponent(codes[i]) + "?redirect=/cart.js", { redirect: "follow" });
          } catch (e) { console.warn("[ctq] warmCart code " + codes[i] + " failed:", e); }
          suppressSlideCart();
        }
      } finally {
        setTimeout(() => maskUpcart(false), 1500);
      }
    }

    async function submitCartFormPost(cfg, items, subscribe, codes) {
      const planId = cfg.monthly_selling_plan ? parseInt(cfg.monthly_selling_plan, 10) : null;
      const adds = items.map(it => {
        const vid = parseInt(cfg.variants[it.product], 10);
        if (!vid) return null;
        const line = { id: vid, quantity: it.qty };
        if (subscribe && planId) line.selling_plan = planId;
        return line;
      }).filter(Boolean);
      if (!adds.length) return;

      try {
        await fetch("/cart/clear.js", { method: "POST", headers: { "Accept": "application/json" } });
      } catch (e) {}

      const form = document.createElement("form");
      form.method = "post";
      form.action = "/cart/add";
      form.style.display = "none";

      adds.forEach((item, i) => {
        Object.entries(item).forEach(([k, v]) => {
          if (v === undefined || v === null) return;
          const inp = document.createElement("input");
          inp.type = "hidden";
          inp.name = "items[" + i + "][" + k + "]";
          inp.value = String(v);
          form.appendChild(inp);
        });
      });

      const trackingParams = new URLSearchParams();
      const passthrough = /^(utm_|gclid$|fbclid$|ttclid$|msclkid$|li_fat_id$|irclickid$|ref$)/i;
      new URLSearchParams(window.location.search).forEach((v, k) => {
        if (passthrough.test(k)) trackingParams.set(k, v);
      });
      if (codes.length) trackingParams.set("discount", codes.join(","));
      const qs = trackingParams.toString();

      const returnTo = document.createElement("input");
      returnTo.type = "hidden";
      returnTo.name = "return_to";
      returnTo.value = "/checkout" + (qs ? ("?" + qs) : "");
      form.appendChild(returnTo);

      document.body.appendChild(form);
      form.submit();
    }

    function addToCart() {
      const items = STACK.map(p => ({ product: p, qty: 1 }));
      const cfg = (window.CTQ_CONFIG && window.CTQ_CONFIG.variants) ? window.CTQ_CONFIG : PRODUCT_CONFIG_DEFAULT;
      const codes = computeCartDiscountCodes();
      const onShopify = isOnShopify();
      const cartUrl = buildCartUrl(cfg, items, true, codes);

      track("cx_quiz_cart_added", {
        cart_payload: items,
        total: 54.60,
        codes: codes,
        subscribe: true,
        cart_url: cartUrl,
        submit_mode: onShopify ? "form_post" : "permalink"
      });

      if (onShopify) {
        setTimeout(() => { submitCartFormPost(cfg, items, true, codes); }, 50);
      } else if (cartUrl) {
        setTimeout(() => { window.location.assign(cartUrl); }, 50);
      }
    }

    /* ---------- init ---------- */
    screens.forEach((s, i) => { if (i === 0) s.classList.add("is-active"); });
    updateProgress();
    updateReceipt();
  }

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll("[data-ctq-quiz-root]").forEach(init);
  });

  window.CTQQuiz = { init };
})();
